/**
 * Toast Jobs/Positions View
 * List display of job roles from Toast POS
 */
import React, { useState, useMemo } from 'react';

const ToastJobsView = ({ jobs, employees, loading }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('position');
  const [sortDirection, setSortDirection] = useState('asc');

  // Get employee count for a job
  const getEmployeeCount = (jobGuid) => {
    return employees.filter(emp =>
      emp.jobReferences?.some(jr => jr.guid === jobGuid) &&
      !emp.deleted && !emp.disabled
    ).length;
  };

  // Handle column header click
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Filter and sort jobs
  const filteredJobs = useMemo(() => {
    let filtered = [...jobs];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(job =>
        job.title?.toLowerCase().includes(term) ||
        job.name?.toLowerCase().includes(term) ||
        job.code?.toLowerCase().includes(term)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let valueA, valueB;

      switch (sortColumn) {
        case 'position':
          valueA = (a.title || a.name || '').toLowerCase();
          valueB = (b.title || b.name || '').toLowerCase();
          break;
        case 'code':
          valueA = (a.code || '').toLowerCase();
          valueB = (b.code || '').toLowerCase();
          break;
        case 'defaultPay':
          valueA = parseFloat(a.defaultWage) || 0;
          valueB = parseFloat(b.defaultWage) || 0;
          break;
        case 'overtimePay':
          valueA = parseFloat(a.overtimeWage) || 0;
          valueB = parseFloat(b.overtimeWage) || 0;
          break;
        case 'employees':
          valueA = getEmployeeCount(a.guid);
          valueB = getEmployeeCount(b.guid);
          break;
        case 'tipped':
          valueA = a.tipped ? 1 : 0;
          valueB = b.tipped ? 1 : 0;
          break;
        default:
          valueA = '';
          valueB = '';
      }

      if (typeof valueA === 'string') {
        const result = valueA.localeCompare(valueB);
        return sortDirection === 'asc' ? result : -result;
      } else {
        const result = valueA - valueB;
        return sortDirection === 'asc' ? result : -result;
      }
    });

    return filtered;
  }, [jobs, searchTerm, sortColumn, sortDirection, employees]);

  const formatWage = (wage) => {
    if (!wage) return '—';
    return `$${parseFloat(wage).toFixed(2)}/hr`;
  };

  const SortHeader = ({ column, label }) => (
    <th onClick={() => handleSort(column)} className="sortable">
      {label}
      {sortColumn === column && (
        <span className="sort-arrow">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
            {sortDirection === 'asc'
              ? <path d="M5 2L9 7H1L5 2Z"/>
              : <path d="M5 8L1 3H9L5 8Z"/>
            }
          </svg>
        </span>
      )}
    </th>
  );

  return (
    <div className="toast-list-container">
      {/* Toolbar */}
      <div className="list-toolbar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search positions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')}>×</button>
          )}
        </div>

        <span className="results-count">{filteredJobs.length} positions</span>
      </div>

      {/* List */}
      {filteredJobs.length === 0 ? (
        <div className="empty-state">
          <h3>No positions found</h3>
          <p>
            {searchTerm
              ? 'Try adjusting your search'
              : 'Job positions will appear here once synced from Toast'}
          </p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <SortHeader column="position" label="Position" />
              <SortHeader column="code" label="Code" />
              <SortHeader column="defaultPay" label="Default Pay" />
              <SortHeader column="overtimePay" label="Overtime Pay" />
              <SortHeader column="employees" label="Employees" />
              <SortHeader column="tipped" label="Tipped" />
            </tr>
          </thead>
          <tbody>
            {filteredJobs.map(job => (
              <tr key={job.guid}>
                <td className="col-name">{job.title || job.name}</td>
                <td className="col-id">{job.code || '—'}</td>
                <td className="col-pay">{formatWage(job.defaultWage)}</td>
                <td className="col-pay">{formatWage(job.overtimeWage)}</td>
                <td>{getEmployeeCount(job.guid)}</td>
                <td>
                  <span className={`status-badge ${job.tipped ? 'active' : ''}`}>
                    {job.tipped ? 'Yes' : 'No'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ToastJobsView;
